import { compareLocalDate } from '../date/local-date.js';
/**
 * The queue's order, and which key decided it.
 *
 * A missing expiry (a credential never on file, or one with no date) sorts
 * before any date: it has been a problem for longer than anything dated.
 */
export function compareQueueEntries(a, b) {
    if (a.cannotWorkToday !== b.cannotWorkToday) {
        return { order: a.cannotWorkToday ? -1 : 1, decidedBy: 'BLOCKING' };
    }
    if (a.minutesAtStake !== b.minutesAtStake) {
        return { order: b.minutesAtStake - a.minutesAtStake, decidedBy: 'WORK_AT_STAKE' };
    }
    if (a.soonestExpiry !== b.soonestExpiry) {
        if (a.soonestExpiry === null)
            return { order: -1, decidedBy: 'SOONEST_EXPIRY' };
        if (b.soonestExpiry === null)
            return { order: 1, decidedBy: 'SOONEST_EXPIRY' };
        return {
            order: compareLocalDate(a.soonestExpiry, b.soonestExpiry),
            decidedBy: 'SOONEST_EXPIRY',
        };
    }
    const byName = a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    if (byName !== 0)
        return { order: byName, decidedBy: 'NAME' };
    const byId = a.caregiverId < b.caregiverId ? -1 : a.caregiverId > b.caregiverId ? 1 : 0;
    return { order: byId, decidedBy: 'NAME' };
}
/** Rank the queue. Candidates with nothing blocking and nothing expiring are left out. */
export function rankComplianceQueue(candidates, horizon) {
    const unranked = candidates
        .filter((candidate) => candidate.blocking.length > 0 || candidate.expiring.length > 0)
        .map((candidate) => {
        const cannotWorkToday = candidate.blocking.length > 0;
        const issues = cannotWorkToday ? candidate.blocking : candidate.expiring;
        const workAtStake = stakeOf(cannotWorkToday ? horizon.from : candidate.blockedFrom, candidate.availability, horizon);
        const keys = {
            caregiverId: candidate.caregiverId,
            name: candidate.name,
            cannotWorkToday,
            minutesAtStake: workAtStake.minutes,
            soonestExpiry: soonestExpiryOf(issues),
        };
        return { candidate, keys, issues, workAtStake };
    });
    const sorted = [...unranked].sort((a, b) => compareQueueEntries(a.keys, b.keys).order);
    return sorted.map((entry, index) => {
        const next = sorted[index + 1];
        const decision = next ? compareQueueEntries(entry.keys, next.keys) : null;
        return {
            rank: index + 1,
            caregiverId: entry.candidate.caregiverId,
            cannotWorkToday: entry.keys.cannotWorkToday,
            blockedFrom: entry.keys.cannotWorkToday ? horizon.from : entry.candidate.blockedFrom,
            issues: entry.issues,
            workAtStake: entry.workAtStake,
            soonestExpiry: entry.keys.soonestExpiry,
            explanation: {
                listed: listedText(entry.keys.cannotWorkToday, entry.candidate.blockedFrom),
                atStake: atStakeText(entry.workAtStake, entry.keys.cannotWorkToday, entry.candidate.blockedFrom, horizon),
                placement: next && decision
                    ? placementText(entry.keys, next.keys, decision.decidedBy)
                    : sorted.length === 1
                        ? 'The only caregiver in the queue.'
                        : 'Last in the queue.',
                decidedBy: decision ? decision.decidedBy : null,
            },
        };
    });
}
function stakeOf(from, availability, horizon) {
    if (from === null || compareLocalDate(from, horizon.to) > 0) {
        return { minutes: 0, from, days: [] };
    }
    const byDay = new Map();
    for (const window of availability) {
        if (compareLocalDate(window.date, from) < 0 || compareLocalDate(window.date, horizon.to) > 0) {
            continue;
        }
        byDay.set(window.date, (byDay.get(window.date) ?? 0) + window.endMinute - window.startMinute);
    }
    const days = [...byDay.entries()]
        .sort(([a], [b]) => compareLocalDate(a, b))
        .map(([date, minutes]) => ({ date, minutes }));
    return { minutes: days.reduce((sum, day) => sum + day.minutes, 0), from, days };
}
function soonestExpiryOf(issues) {
    let soonest = null;
    for (const issue of issues) {
        if (issue.expiresAt === null)
            return null;
        if (soonest === null || compareLocalDate(issue.expiresAt, soonest) < 0)
            soonest = issue.expiresAt;
    }
    return soonest;
}
/** "7 h", "7 h 30 min", "45 min". */
export function hoursText(minutes) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours === 0)
        return `${rest} min`;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
function listedText(cannotWorkToday, blockedFrom) {
    if (cannotWorkToday)
        return 'Cannot work today.';
    return blockedFrom === null
        ? 'A credential they need to work is about to run out.'
        : `Cannot work from ${blockedFrom}.`;
}
function atStakeText(stake, cannotWorkToday, blockedFrom, horizon) {
    if (stake.minutes > 0) {
        const days = stake.days.length === 1 ? '1 day' : `${stake.days.length} days`;
        return `${hoursText(stake.minutes)} they offered the rota from ${String(stake.from)} to ${horizon.to} is lost, across ${days}.`;
    }
    if (!cannotWorkToday && blockedFrom === null) {
        return 'Nothing is counted as at stake: the first day they cannot work is not known.';
    }
    if (!cannotWorkToday && compareLocalDate(blockedFrom, horizon.to) > 0) {
        return `It runs out after ${horizon.to}, so nothing they offered up to then is lost.`;
    }
    return `They offered the rota nothing from ${String(stake.from)} to ${horizon.to}, so nothing breaks yet.`;
}
function placementText(entry, next, decidedBy) {
    switch (decidedBy) {
        case 'BLOCKING':
            return 'Above the next caregiver because this one cannot work today and the next one still can.';
        case 'WORK_AT_STAKE':
            return `Above the next caregiver because more work is at stake: ${hoursText(entry.minutesAtStake)} against ${hoursText(next.minutesAtStake)}.`;
        case 'SOONEST_EXPIRY': {
            const mine = entry.soonestExpiry === null ? 'never on file' : `${entry.soonestExpiry}`;
            // Never null: a missing expiry sorts first, so the entry below one
            // decided on expiry always has a date.
            const theirs = String(next.soonestExpiry);
            return entry.cannotWorkToday
                ? `Above the next caregiver: the same work is at stake, and this lapse is older — ${mine} against ${theirs}.`
                : `Above the next caregiver: the same work is at stake, and this runs out sooner — ${mine} against ${theirs}.`;
        }
        case 'NAME':
            return 'Level with the next caregiver on all three counts, so listed by name.';
    }
}
//# sourceMappingURL=compliance-queue.js.map